import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import { useCustomReducer } from '../../../../base/customHooks'
import { BaseComponentWithContextProps, componentWithContext } from '../../../../base/customHooks/componentHOC'
import { CreditApplication, CreditListFilter, CreditStatus, StatusTexts } from './models'
import { GetCreditListApi } from './services/getCreditListApi'
import { GetBasketCreditListApi } from './services/getBasketCreditListApi'
import { CancelCreditApi } from './services/cancelCreditApi'
import styles from './CreditPanel.module.scss'
import classes from '../../../../components/IPFile/IPFile.module.scss'
import { getUploadFileRequest } from '../../../../base/utils/dataHelper'
import { AddDocumentApi } from '../services/addDocumentApi'
import { DeleteDocumentApi } from '../services/deleteDocumentApi'

// Tab türleri
type TabType = 'serial' | 'basket'

// Sepet kredisi için interface
interface BasketCreditApplication {
  Id: number
  OrderId: string
  DealerCode: string
  CompanyCode: string
  CustomerGsmNumber: string
  CustomerTcNo?: string
  CreditAmount: number
  CreditAmountTL: number
  CurrencyType: string
  ExchangeRate: number
  CompayApplicationId: string
  Status: CreditStatus
  StatusDisplay: string
  ErrorMessage: string | null
  CreatedAt: string
  UpdatedAt: string | null
  ProductCount: number
  Products?: {
    ProductCode: string
    ProductName: string
    Quantity: number
    UnitPrice: number
    TotalPrice: number
  }[]
}

interface CreditPanelState {
  activeTab: TabType
  loading: boolean
  credits: CreditApplication[]
  basketCredits: BasketCreditApplication[]
  totalCount: number
  basketTotalCount: number
  pageNumber: number
  pageSize: number
  // Filtreler
  startDate: string
  endDate: string
  status: CreditStatus | ''
  serialNumber: string
  orderId: string
  // İstatistikler
  stats: {
    total: number
    pending: number
    approved: number
    rejected: number
  }
  basketStats: {
    total: number
    pending: number
    approved: number
    rejected: number
  }
  cancellingOrderId: string | null
}

function CreditPanel(props: BaseComponentWithContextProps) {
  const navigate = useNavigate()

  const getInitialState = (): CreditPanelState => {
    // Son 30 günü varsayılan olarak ayarla
    const endDate = new Date()
    const startDate = new Date()
    startDate.setDate(startDate.getDate() - 30)

    return {
      activeTab: 'serial',
      loading: false,
      credits: [],
      basketCredits: [],
      totalCount: 0,
      basketTotalCount: 0,
      pageNumber: 1,
      pageSize: 20,
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
      status: '',
      serialNumber: '',
      orderId: '',
      stats: {
        total: 0,
        pending: 0,
        approved: 0,
        rejected: 0,
      },
      basketStats: {
        total: 0,
        pending: 0,
        approved: 0,
        rejected: 0,
      },
      cancellingOrderId: null,
    }
  }

  const [state, dispatchState] = useCustomReducer<CreditPanelState>(getInitialState())

  useEffect(() => {
    if (state.activeTab === 'serial') {
      fetchCredits()
    } else {
      fetchBasketCredits()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.pageNumber, state.activeTab])

  const fetchCredits = async () => {
    dispatchState({ loading: true })

    try {
      const filter: CreditListFilter = {
        DealerCode: props.user.AccountCode,
        StartDate: state.startDate,
        EndDate: state.endDate,
        Status: state.status || undefined,
        SerialNumber: state.serialNumber || undefined,
        OrderId: state.orderId || undefined,
      }

      const response = await new GetCreditListApi({}).getCreditList(filter)

      // İstatistikleri backend'den al
      const stats = {
        total: response.Statistics?.TotalCount || response.Items?.length || 0,
        pending: response.Statistics?.PendingCount || 0,
        approved: response.Statistics?.ApprovedCount || 0,
        rejected: response.Statistics?.RejectedCount || 0,
      }

      dispatchState({
        loading: false,
        credits: response.Items || [],
        totalCount: response.TotalCount || 0,
        stats,
      })
    } catch (err: any) {
      console.error('Error fetching credits:', err)
      dispatchState({ loading: false })
      Swal.fire({
        icon: 'error',
        title: 'Hata',
        text: err.description || 'Kredi listesi alınamadı.',
      })
    }
  }

  const fetchBasketCredits = async () => {
    dispatchState({ loading: true })

    try {
      const filter = {
        DealerCode: props.user.AccountCode,
        StartDate: state.startDate,
        EndDate: state.endDate,
        Status: state.status || undefined,
        OrderId: state.orderId || undefined,
      }

      const response = await new GetBasketCreditListApi({}).getCreditList(filter)

      // İstatistikleri backend'den al
      const basketStats = {
        total: response.Statistics?.TotalCount || response.Items?.length || 0,
        pending: response.Statistics?.PendingCount || 0,
        approved: response.Statistics?.ApprovedCount || 0,
        rejected: response.Statistics?.RejectedCount || 0,
      }

      dispatchState({
        loading: false,
        basketCredits: response.Items || [],
        basketTotalCount: response.TotalCount || 0,
        basketStats,
      })
    } catch (err: any) {
      console.error('Error fetching basket credits:', err)
      dispatchState({ loading: false })
      Swal.fire({
        icon: 'error',
        title: 'Hata',
        text: err.description || 'Sepet kredileri alınamadı.',
      })
    }
  }

  const handleTabChange = (tab: TabType) => {
    dispatchState({ activeTab: tab, pageNumber: 1 })
  }

  const handleFilter = () => {
    dispatchState({ pageNumber: 1 })
    if (state.activeTab === 'serial') {
      fetchCredits()
    } else {
      fetchBasketCredits()
    }
  }

  const handleClearFilter = () => {
    const endDate = new Date()
    const startDate = new Date()
    startDate.setDate(startDate.getDate() - 30)

    dispatchState({
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
      status: '',
      serialNumber: '',
      orderId: '',
      pageNumber: 1,
    })

    setTimeout(() => {
      if (state.activeTab === 'serial') {
        fetchCredits()
      } else {
        fetchBasketCredits()
      }
    }, 100)
  }

  const handleCancelCredit = async (credit: CreditApplication) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Kredi İptali',
      html: `
        <div style="text-align: left;">
          <p><strong>Başvuru No:</strong> ${credit.CompayApplicationId}</p>
          <p><strong>Ürün:</strong> ${credit.ProductCode.replaceAll('@@', ' | ')}</p>
          <p><strong>Tutar:</strong> ${formatCurrency(credit.CreditAmountTL)}</p>
          <br/>
          <p style="color: #dc3545;">Bu kredi başvurusunu iptal etmek istediğinize emin misiniz?</p>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'İptal Et',
      cancelButtonText: 'Vazgeç',
      confirmButtonColor: '#dc3545',
    })

    if (!result.isConfirmed) return

    dispatchState({ cancellingOrderId: credit.CompayApplicationId })

    try {
      const response = await new CancelCreditApi({}).cancelCredit({
        OrderId: credit.CompayApplicationId,
        SerialNumber: credit.SerialNumber,
        DealerCode: props.user.AccountCode,
      })

      dispatchState({ cancellingOrderId: null })

      if (response.Success) {
        Swal.fire({
          icon: 'success',
          title: 'Başarılı',
          text: 'Kredi başvurusu iptal edildi.',
        })
        fetchCredits()
      } else {
        Swal.fire({
          icon: 'error',
          title: 'İptal Başarısız',
          text: response.Message || 'İşlem gerçekleştirilemedi.',
        })
      }
    } catch (err: any) {
      dispatchState({ cancellingOrderId: null })
      Swal.fire({
        icon: 'error',
        title: 'Hata',
        text: err.description || 'Bağlantı hatası oluştu.',
      })
    }
  }

  const formatCurrency = (amount: number, currency: string = 'TL'): string => {
    return (
      amount.toLocaleString('tr-TR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }) +
      ' ' +
      currency
    )
  }

  const formatDate = (dateStr: string): { date: string; time: string } => {
    const date = new Date(dateStr)
    return {
      date: date.toLocaleDateString('tr-TR'),
      time: date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }),
    }
  }

  const getStatusClass = (status: CreditStatus): string => {
    return status.toLowerCase()
  }

  // Tab yapısı için render fonksiyonu
  const renderTabs = () => (
    <div className={styles.tabContainer}>
      <button
        className={`${styles.tabButton} ${state.activeTab === 'basket' ? styles.active : ''}`}
        onClick={() => handleTabChange('basket')}
      >
        <i className='fas fa-shopping-cart'></i>
        Sepet Kredileri
      </button>
      <button
        className={`${styles.tabButton} ${state.activeTab === 'serial' ? styles.active : ''}`}
        onClick={() => handleTabChange('serial')}
      >
        <i className='fas fa-barcode'></i>
        Seri No Kredileri
      </button>
    </div>
  )

  const renderStats = () => {
    const currentStats = state.activeTab === 'serial' ? state.stats : state.basketStats
    return (
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={`${styles.statIcon} ${styles.total}`}>
            <i className='fas fa-list'></i>
          </div>
          <div className={styles.statContent}>
            <div className={styles.statValue}>{currentStats.total}</div>
            <div className={styles.statLabel}>Toplam Başvuru</div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={`${styles.statIcon} ${styles.pending}`}>
            <i className='fas fa-clock'></i>
          </div>
          <div className={styles.statContent}>
            <div className={styles.statValue}>{currentStats.pending}</div>
            <div className={styles.statLabel}>Beklemede</div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={`${styles.statIcon} ${styles.approved}`}>
            <i className='fas fa-check'></i>
          </div>
          <div className={styles.statContent}>
            <div className={styles.statValue}>{currentStats.approved}</div>
            <div className={styles.statLabel}>Onaylanan</div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={`${styles.statIcon} ${styles.rejected}`}>
            <i className='fas fa-times'></i>
          </div>
          <div className={styles.statContent}>
            <div className={styles.statValue}>{currentStats.rejected}</div>
            <div className={styles.statLabel}>Reddedilen</div>
          </div>
        </div>
      </div>
    )
  }

  const renderFilters = () => (
    <div className={styles.filterSection}>
      <div className={styles.filterTitle}>
        <i className='fas fa-filter'></i>
        Filtreler
      </div>
      <div className={styles.filterGrid}>
        <div className={styles.filterItem}>
          <label>Başlangıç Tarihi</label>
          <input type='date' value={state.startDate} onChange={e => dispatchState({ startDate: e.target.value })} />
        </div>
        <div className={styles.filterItem}>
          <label>Bitiş Tarihi</label>
          <input type='date' value={state.endDate} onChange={e => dispatchState({ endDate: e.target.value })} />
        </div>
        <div className={styles.filterItem}>
          <label>Durum</label>
          <select value={state.status} onChange={e => dispatchState({ status: e.target.value as CreditStatus | '' })}>
            <option value=''>Tümü</option>
            <option value='PENDING'>Beklemede</option>
            <option value='APPROVED'>Onaylandı</option>
            <option value='REJECTED'>Reddedildi</option>
            <option value='CANCELLED'>İptal Edildi</option>
            <option value='EXPIRED'>Süresi Doldu</option>
          </select>
        </div>
        {/* Seri Numarası filtresi sadece serial tabında göster */}
        {state.activeTab === 'serial' && (
          <div className={styles.filterItem}>
            <label>Seri Numarası</label>
            <input
              type='text'
              placeholder='Seri numarası girin...'
              value={state.serialNumber}
              onChange={e => dispatchState({ serialNumber: e.target.value })}
            />
          </div>
        )}
        <div className={styles.filterItem}>
          <label>Başvuru Numarası</label>
          <input
            type='text'
            placeholder={state.activeTab === 'serial' ? 'SC...' : 'BC...'}
            value={state.orderId}
            onChange={e => dispatchState({ orderId: e.target.value })}
          />
        </div>
      </div>
      <div className={styles.filterActions}>
        <button className={`${styles.btnFilter} ${styles.secondary}`} onClick={handleClearFilter}>
          <i className='fas fa-eraser'></i>
          Temizle
        </button>
        <button className={`${styles.btnFilter} ${styles.primary}`} onClick={handleFilter}>
          <i className='fas fa-search'></i>
          Filtrele
        </button>
      </div>
    </div>
  )

  const renderTable = () => {
    if (state.loading) {
      return (
        <div className={styles.loadingState}>
          <div className={styles.spinner}></div>
          <p>Yükleniyor...</p>
        </div>
      )
    }

    if (state.activeTab === 'serial') {
      return renderSerialTable()
    } else {
      return renderBasketTable()
    }
  }

  const renderSerialTable = () => {
    if (!state.credits.length) {
      return (
        <div className={styles.emptyState}>
          <i className='fas fa-inbox'></i>
          <h4>Kayıt Bulunamadı</h4>
          <p>Seçilen kriterlere uygun kredi başvurusu bulunmamaktadır.</p>
          <button className={styles.newApplicationBtn} onClick={() => navigate('/myspecial/serial-credit-application')}>
            <i className='fas fa-plus'></i>
            Yeni Başvuru Oluştur
          </button>
        </div>
      )
    }

    const addDocument = async (e: any, id: number) => {
      const fileList: any[] = await getUploadFileRequest(e)
      for (const fileData of fileList) {
        if (fileData.size > 50000000) {
          return Swal.fire('Hata', 'Dosya boyutu 50 MB\'den küçük olmalıdır!', 'error')
        }

        const allowedTypes = ['.jpeg', '.png', '.jpg', '.vnd.openxmlformats-officedocument.wordprocessingml.document', '.pdf']
        if (!allowedTypes.includes(fileData.Extension)) {
          return Swal.fire('Hata', 'Hatalı dosya formatı! Sayfayı yeniledikten sonra tekrar deneyiniz.', 'error')
        }
        if (fileData.Extension === '.vnd.openxmlformats-officedocument.wordprocessingml.document')
          fileData.Extension = ".docx"
      }

      var file = fileList.map((fileData, index) => ({
        Id: index,
        Label: '',
        Description: '',
        File: fileData,
      }))

      const data: any = {
        Id: id,
        File: file[0].File
      }

      const addImageApi = new AddDocumentApi({})
      try {
        const result = await addImageApi.addDocument(data)
        if (result) {
          Swal.fire({
            icon: 'success',
            title: 'Başarılı',
            text: 'Fatura başarıyla yüklendi.',
          })
          fetchCredits()
        }
      } catch (err: any) {
        Swal.fire({
          icon: 'error',
          title: err.description,
          showConfirmButton: true,
          allowOutsideClick: false,
        })
      }
    }

    const deleteDocument = async (app: string, doc: string, id: number) => {
      const data: any = {
        Id: id,
        Document: doc
      }

      Swal.fire({
        icon: 'question',
        title: `${app} başvurusuna ait faturayı silmek istediğinize emin misiniz?`,
        showConfirmButton: true,
        allowOutsideClick: false,
        showDenyButton: true,
        confirmButtonText: 'Evet',
        denyButtonText: 'Hayır',
      }).then(async result => {
        if (result.isConfirmed) {
          const deleteImageApi = new DeleteDocumentApi({})
          try {
            const result = await deleteImageApi.deleteDocument(data)
            if (result) {
              Swal.fire({
                icon: 'success',
                title: 'Başarılı',
                text: 'Fatura başarıyla silindi.',
              })
              fetchCredits()
            }
          } catch (err: any) {
            Swal.fire({
              icon: 'error',
              title: err.description,
              showConfirmButton: true,
              allowOutsideClick: false,
            })
          }
        }
      })
    }

    return (
      <div className={styles.tableWrapper}>
        <table>
          <thead>
            <tr>
              <th>Başvuru No</th>
              <th>Ürün Bilgisi</th>
              <th>Tutar</th>
              <th>Müşteri Tel</th>
              <th>Durum</th>
              <th>Tarih</th>
              <th>İşlem</th>
            </tr>
          </thead>
          <tbody>
            {state.credits.map((credit, index) => {
              const dateInfo = formatDate(credit.CreatedAt)
              return (
                <tr key={credit.Id}>
                  <td>
                    <strong>{credit.CompayApplicationId}</strong>
                  </td>
                  <td>
                    <div className={styles.productInfo}>
                      <div className={styles.productCode}>{credit.ProductCode.replaceAll('@@', ' | ')}</div>
                      <div className={styles.serialNumber}>{credit.SerialNumber.replaceAll('@@', ' | ')}</div>
                    </div>
                  </td>
                  <td>
                    <div className={styles.amountInfo}>
                      <div className={styles.amountTL}>{formatCurrency(credit.CreditAmountTL)}</div>
                      {credit.CurrencyType !== 'TL' && credit.CurrencyType !== 'TRY' && (
                        <div className={styles.amountOriginal}>
                          {formatCurrency(credit.CreditAmount, credit.CurrencyType)}
                        </div>
                      )}
                    </div>
                  </td>
                  <td>{credit.CustomerGsmNumber}</td>
                  <td>
                    <span className={`${styles.statusBadge} ${styles[getStatusClass(credit.Status)]}`}>
                      {credit.StatusDisplay || StatusTexts[credit.Status]}
                    </span>
                  </td>
                  <td>
                    <div className={styles.dateInfo}>
                      <div className={styles.date}>{dateInfo.date}</div>
                      <div className={styles.time}>{dateInfo.time}</div>
                    </div>
                  </td>
                  <td>
                    {(credit.Status === 'PENDING' || credit.Status === 'SMS_SENT') && (
                      <button
                        className={`${styles.actionBtn} ${styles.cancel}`}
                        onClick={() => handleCancelCredit(credit)}
                        disabled={state.cancellingOrderId === credit.CompayApplicationId}
                      >
                        {state.cancellingOrderId === credit.CompayApplicationId ? (
                          <i className='fas fa-spinner fa-spin'></i>
                        ) : (
                          <>
                            <i className='fas fa-ban'></i>
                            İptal
                          </>
                        )}
                      </button>
                    )}
                    {credit.Status === 'REJECTED' && credit.ErrorMessage && (
                      <button
                        className={`${styles.actionBtn} ${styles.view}`}
                        onClick={() =>
                          Swal.fire({
                            icon: 'info',
                            title: 'Ret Sebebi',
                            text: credit.ErrorMessage || 'Bilinmeyen hata',
                          })
                        }
                      >
                        <i className='fas fa-eye'></i>
                        Detay
                      </button>
                    )}
                    {
                      credit.Status === 'APPROVED' && (
                        <>
                          {
                            !credit.Document &&
                            <>
                              <input
                                type='file'
                                onChange={(event) => {
                                  addDocument(event, credit.Id)
                                }}
                                multiple={false}
                                id={`ImageFile${index}`}
                                style={{ display: 'none' }}
                              />
                              <label htmlFor={`ImageFile${index}`} style={{ cursor: 'pointer', margin: '3px', color: '#007bff', fontSize: '16px' }} title='Fatura Yükle'>
                                <i className='icon-upload'></i>
                              </label>
                            </>
                          }

                          {credit.Document && credit.Document.length > 0 &&
                            <>
                              <i className='icon-file' style={{ margin: '3px', cursor: 'pointer', color: 'green' }} onClick={() => {
                                window.open(credit.Document, '_blank');
                              }} title='Faturayı Görüntüle'></i>
                              <i className='icon-trash-2' style={{ cursor: 'pointer', color: 'red', marginLeft: '5px' }} onClick={() => { deleteDocument(credit.CompayApplicationId, credit.Document!, credit.Id) }} title='Faturayı Sil'></i>
                            </>
                          }
                        </>
                      )
                    }
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    )
  }

  const renderBasketTable = () => {
    if (!state.basketCredits.length) {
      return (
        <div className={styles.emptyState}>
          <i className='fas fa-shopping-cart'></i>
          <h4>Kayıt Bulunamadı</h4>
          <p>Seçilen kriterlere uygun sepet kredisi bulunmamaktadır.</p>
        </div>
      )
    }

    return (
      <div className={styles.tableWrapper}>
        <table>
          <thead>
            <tr>
              <th>Başvuru No</th>
              <th>Sipariş No</th>
              <th>Ürün Sayısı</th>
              <th>Tutar</th>
              <th>Durum</th>
              <th>Tarih</th>
              <th>İşlem</th>
            </tr>
          </thead>
          <tbody>
            {state.basketCredits.map(credit => {
              const dateInfo = formatDate(credit.CreatedAt)
              return (
                <tr key={credit.Id}>
                  <td>
                    <strong>{credit.CompayApplicationId}</strong>
                  </td>
                  <td>
                    <div className={styles.productInfo}>
                      <div className={styles.productCode}>{credit.OrderId}</div>
                    </div>
                  </td>
                  <td>
                    <span className={styles.productCount}>{credit.ProductCount} Ürün</span>
                  </td>
                  <td>
                    <div className={styles.amountInfo}>
                      <div className={styles.amountTL}>{formatCurrency(credit.CreditAmountTL)}</div>
                      {credit.CurrencyType !== 'TL' && credit.CurrencyType !== 'TRY' && (
                        <div className={styles.amountOriginal}>
                          {formatCurrency(credit.CreditAmount, credit.CurrencyType)}
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className={`${styles.statusBadge} ${styles[getStatusClass(credit.Status)]}`}>
                      {credit.StatusDisplay || StatusTexts[credit.Status]}
                    </span>
                  </td>
                  <td>
                    <div className={styles.dateInfo}>
                      <div className={styles.date}>{dateInfo.date}</div>
                      <div className={styles.time}>{dateInfo.time}</div>
                    </div>
                  </td>
                  <td>
                    {credit.Products && credit.Products.length > 0 && (
                      <button
                        className={`${styles.actionBtn} ${styles.view}`}
                        onClick={() =>
                          Swal.fire({
                            icon: 'info',
                            title: 'Ürün Detayları',
                            html: `
                              <div style="text-align: left; max-height: 300px; overflow-y: auto;">
                                ${credit
                                .Products!.map(
                                  p => `
                                  <div style="padding: 8px 0; border-bottom: 1px solid #eee;">
                                    <strong>${p.ProductCode}</strong><br/>
                                    <small>${p.ProductName}</small><br/>
                                    <span>${p.Quantity} x ${p.UnitPrice.toLocaleString(
                                    'tr-TR'
                                  )} TL = ${p.TotalPrice.toLocaleString('tr-TR')} TL</span>
                                  </div>
                                `
                                )
                                .join('')}
                              </div>
                            `,
                          })
                        }
                      >
                        <i className='fas fa-eye'></i>
                        Detay
                      </button>
                    )}
                    {credit.Status === 'REJECTED' && credit.ErrorMessage && (
                      <button
                        className={`${styles.actionBtn} ${styles.view}`}
                        onClick={() =>
                          Swal.fire({
                            icon: 'info',
                            title: 'Ret Sebebi',
                            text: credit.ErrorMessage || 'Bilinmeyen hata',
                          })
                        }
                      >
                        <i className='fas fa-exclamation-circle'></i>
                        Hata
                      </button>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    )
  }

  return (
    <div className={styles.creditPanel}>
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <h2>
            <i className='fas fa-credit-card'></i>
            Kredi Paneli
          </h2>
          <button className={styles.newApplicationBtn} onClick={() => navigate('/myspecial/serial-credit-application')}>
            <i className='fas fa-plus'></i>
            Yeni Başvuru
          </button>
        </div>

        {/* Tab Menüsü */}
        {renderTabs()}

        {/* İstatistikler */}
        {renderStats()}

        {/* Filtreler */}
        {renderFilters()}

        {/* Tablo */}
        <div className={styles.tableSection}>
          <div className={styles.tableHeader}>
            <span className={styles.tableTitle}>
              {state.activeTab === 'serial' ? 'Seri No Kredi Başvuruları' : 'Sepet Kredi Başvuruları'}
            </span>
            <span className={styles.tableCount}>
              {state.activeTab === 'serial' ? state.totalCount : state.basketTotalCount} kayıt bulundu
            </span>
          </div>
          {renderTable()}
        </div>
      </div>
    </div>
  )
}

export default componentWithContext(CreditPanel)
